import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Pencil, Trash2, Phone, Mail, MapPin, Building2, User } from "lucide-react";

const empty = { type: "particulier", name: "", contact_name: "", address: "", phone: "", email: "", siret: "", notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async (q = "") => {
    const { data } = await api.get("/clients", { params: q ? { search: q } : {} });
    setClients(data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (params.get("new") === "1") { openNew(); setParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Le nom est obligatoire"); return; }
    try {
      const payload = { ...form };
      delete payload.id; delete payload.created_at;
      if (editId) await api.put(`/clients/${editId}`, payload);
      else await api.post("/clients", payload);
      toast.success(editId ? "Client modifié" : "Client créé");
      setOpen(false);
      load(search);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/clients/${deleteId}`);
      toast.success("Client supprimé");
      setDeleteId(null);
      load(search);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Clients</h1>
          <p className="text-slate-600">{clients.length} client(s)</p>
        </div>
        <Button data-testid="add-client-button" onClick={openNew} className="h-12 font-bold bg-[#16A34A] hover:bg-[#15803D]">
          <Plus size={20} className="mr-1" /> Nouveau client
        </Button>
      </div>

      <div className="relative">
        <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="client-search-input"
          placeholder="Rechercher un client…"
          className="h-12 pl-10 text-base bg-white"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {clients.length === 0 && <p className="text-slate-500">Aucun client trouvé.</p>}
        {clients.map((c) => (
          <div key={c.id} data-testid={`client-card-${c.id}`} className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {c.type === "professionnel" ? <Building2 size={20} className="text-[#0284C7] shrink-0" /> : <User size={20} className="text-[#0284C7] shrink-0" />}
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 truncate">{c.name}</div>
                  {c.contact_name && <div className="text-sm text-slate-500 truncate">{c.contact_name}</div>}
                </div>
              </div>
              <span className="text-[10px] font-semibold uppercase px-2 py-1 rounded bg-slate-100 text-slate-600 shrink-0">
                {c.type === "professionnel" ? "Pro" : "Particulier"}
              </span>
            </div>
            <div className="mt-3 space-y-1 text-sm text-slate-600">
              {c.address && <div className="flex items-center gap-2"><MapPin size={15} /> <span className="truncate">{c.address}</span></div>}
              {c.phone && <div className="flex items-center gap-2"><Phone size={15} /> {c.phone}</div>}
              {c.email && <div className="flex items-center gap-2"><Mail size={15} /> <span className="truncate">{c.email}</span></div>}
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} className="flex-1 border-[#0284C7] text-[#0284C7]">
                <Pencil size={16} className="mr-1" /> Modifier
              </Button>
              <Button size="sm" variant="outline" data-testid={`delete-client-${c.id}`} onClick={() => setDeleteId(c.id)} className="border-red-300 text-red-600">
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editId ? "Modifier le client" : "Nouveau client"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Type de client</Label>
              <Select value={form.type} onValueChange={set("type")}>
                <SelectTrigger data-testid="client-type-select" className="h-12"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="particulier">Particulier</SelectItem>
                  <SelectItem value="professionnel">Professionnel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nom / Société *</Label>
              <Input data-testid="client-name-input" className="h-12" value={form.name} onChange={set("name")} />
            </div>
            <div className="space-y-2">
              <Label>Prénom / Contact</Label>
              <Input data-testid="client-contact-input" className="h-12" value={form.contact_name} onChange={set("contact_name")} />
            </div>
            <div className="space-y-2">
              <Label>Adresse chantier</Label>
              <Input data-testid="client-address-input" className="h-12" value={form.address} onChange={set("address")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input data-testid="client-phone-input" className="h-12" value={form.phone} onChange={set("phone")} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input data-testid="client-email-input" className="h-12" value={form.email} onChange={set("email")} />
              </div>
            </div>
            {form.type === "professionnel" && (
              <div className="space-y-2">
                <Label>SIRET</Label>
                <Input data-testid="client-siret-input" className="h-12" value={form.siret} onChange={set("siret")} />
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes (accès chantier, étage, contraintes)</Label>
              <Textarea data-testid="client-notes-input" value={form.notes} onChange={set("notes")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-12">Annuler</Button>
            <Button data-testid="save-client-button" onClick={save} className="h-12 font-bold bg-[#16A34A] hover:bg-[#15803D]">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce client ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-client" onClick={remove} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
